import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STEPS, stepIndex, type StepId } from '@/lib/campaignSteps';

interface CampaignStepperProps {
  current: StepId;
  isReachable: (id: StepId) => boolean;
  isComplete: (id: StepId) => boolean;
  onSelect: (id: StepId) => void;
}

/**
 * The numbered, named progress bar at the top of a campaign's page.
 *
 * Wide screens get every step with its name; below `md` six labels do not fit, so it collapses to
 * "Step 3 of 6 · Delivery channel" over a segmented bar. The segments are flex children rather than
 * a transformed Progress indicator, so they fill from the correct side in RTL as well.
 */
export default function CampaignStepper({ current, isReachable, isComplete, onSelect }: CampaignStepperProps) {
  const { t } = useTranslation();
  const currentIndex = stepIndex(current);
  const currentStep = STEPS[currentIndex];

  return (
    <nav aria-label={t('campaigns.steps.aria', 'Campaign setup steps')} className="mb-6">
      <ol className="hidden md:flex items-start">
        {STEPS.map((step, i) => {
          const isCurrent = step.id === current;
          const complete = !isCurrent && isComplete(step.id);
          const clickable = !isCurrent && isReachable(step.id);
          const isLast = i === STEPS.length - 1;

          return (
            <li key={step.id} className={cn('flex items-start', isLast ? 'flex-none' : 'flex-1')}>
              <button
                type="button"
                onClick={() => onSelect(step.id)}
                disabled={!clickable}
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  'group flex w-24 flex-col items-center gap-2 rounded-md text-center outline-none',
                  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background',
                  clickable ? 'cursor-pointer' : 'cursor-default',
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                    isCurrent && 'border-primary bg-primary text-primary-foreground shadow-sm',
                    complete && 'border-primary bg-primary/10 text-primary',
                    !isCurrent && !complete && clickable && 'border-muted-foreground/40 text-foreground group-hover:border-primary',
                    !isCurrent && !complete && !clickable && 'border-border text-muted-foreground/60',
                  )}
                >
                  {complete ? <Check className="h-4 w-4" aria-hidden="true" /> : i + 1}
                </span>
                <span
                  className={cn(
                    'text-xs leading-tight',
                    isCurrent ? 'font-semibold text-foreground' : 'font-medium',
                    !isCurrent && (clickable || complete) && 'text-muted-foreground group-hover:text-foreground',
                    !isCurrent && !clickable && !complete && 'text-muted-foreground/60',
                  )}
                >
                  {t(step.labelKey, step.label)}
                  {complete && <span className="sr-only"> ({t('campaigns.steps.done', 'done')})</span>}
                </span>
              </button>
              {!isLast && (
                <span
                  aria-hidden="true"
                  className={cn(
                    // Centred on the 36px circle: half its height minus half the line.
                    'mt-[17px] h-0.5 flex-1 rounded-full',
                    i < currentIndex ? 'bg-primary' : 'bg-border',
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>

      <div className="md:hidden space-y-2">
        <p className="text-sm">
          <span className="text-muted-foreground">
            {t('campaigns.step_of', 'Step {{current}} of {{total}}', { current: currentIndex + 1, total: STEPS.length })}
          </span>
          <span className="text-muted-foreground"> · </span>
          <span className="font-semibold">{t(currentStep.labelKey, currentStep.label)}</span>
        </p>
        <div className="flex gap-1" aria-hidden="true">
          {STEPS.map((step, i) => (
            <span
              key={step.id}
              className={cn('h-1.5 flex-1 rounded-full', i <= currentIndex ? 'bg-primary' : 'bg-muted')}
            />
          ))}
        </div>
      </div>
    </nav>
  );
}
